import React, { useState, useEffect, useMemo, memo, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  getOrdersFormError,
  getOrdersFormLoading,
  getOrdersFormData,
  getVisibleFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getOrdersEnums, getOrdersForm } from '../../../store/order/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { CardTabs } from './ui/tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import FormsHeader from './ui/forms/form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { MeasurementModal } from './ui/modals/modal-measurement'
import { ManufactureModal } from './ui/modals/modal-manufacture'
import { DeliveryModal } from './ui/modals/modal-delivery'
import { Result, Skeleton } from 'antd'
import { clearOrdersFormData } from '../../../store/order/actions'

function DetailPage() {
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()
  const entityType = 'order'
  const ordersFormIsLoading = useSelector(getOrdersFormLoading)
  const ordersFormError = useSelector(getOrdersFormError)
  const ordersFormData = useSelector(getOrdersFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult, createClaim } = useSaveResult()
  const [enumsRequested, setEnumsRequested] = useState(false)
  const [forceRenderKey, setForceRenderKey] = useState(0)
  const [isOpenModal, setIsOpenModal] = useState(false)

  const pageTitle = useMemo(() => `Заказ №${id}`, [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/orders',
        title: 'Заказы',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const handleNextStageOnClick = useCallback(async () => {
    await saveResult(
      id,
      visibleFormData,
      ordersFormData?.stages?.current,
      false,
      () => {
        return
      },
      true,
      undefined,
    )
  }, [id, saveResult, visibleFormData, ordersFormData])

  const handleToClaim = useCallback(async () => {
    await createClaim(id)
  }, [createClaim, id])

  const handleSetDone = useCallback(async () => {
    await saveResult(
      id,
      visibleFormData,
      ordersFormData?.stages?.current,
      false,
      () => {
        return
      },
      true,
      'done',
    )
  }, [id, saveResult, visibleFormData, ordersFormData])

  const handleCancel = useCallback(() => {
    setIsOpenModal(false)
  }, [setIsOpenModal])

  const handleOk = useCallback(
    async modalFormData => {
      if (!visibleFormData) {
        return
      }
      const updatedVisibleFormData = {
        ...visibleFormData,
        modalFieldsSchema: modalFormData,
        modalValid: true,
      }
      await saveResult(
        id,
        updatedVisibleFormData,
        ordersFormData?.stages?.current,
        isOpenModal,
        setIsOpenModal,
        true,
        undefined,
      )
      setForceRenderKey(prev => prev + 1)
      setIsOpenModal(false)
    },
    [setIsOpenModal, saveResult, visibleFormData, ordersFormData, id, isOpenModal],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      setEnumsRequested(false)
      dispatch(getOrdersForm(id))
    }
    return () => {
      mounted = false
      dispatch(clearOrdersFormData())
    }
  }, [id, dispatch])

  useEffect(() => {
    if (ordersFormData && id && !ordersFormError && !ordersFormIsLoading && !enumsRequested) {
      setEnumsRequested(true)
      if (id) {
        dispatch(getOrdersEnums(id))
      }
    }
  }, [ordersFormData, ordersFormError, ordersFormIsLoading, enumsRequested, id, dispatch])

  if (!id) return null

  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      {ordersFormIsLoading && <Skeleton active />}
      {!ordersFormIsLoading && ordersFormError && !ordersFormData && (
        <Result
          status={typeof ordersFormError === 'object' ? ordersFormError.statusCode : 'error'}
          title="Ошибка"
          subTitle={typeof ordersFormError === 'object' ? ordersFormError.message : ordersFormError}
        />
      )}
      {ordersFormData && (
        <>
          <DefaultJsonFormContext.Provider value={defaultProps}>
            <FormsHeader
              id={id}
              stage={ordersFormData?.stages?.current}
              nextStageOnClick={handleNextStageOnClick}
              toClaim={handleToClaim}
              isDisabled={false}
              withoutMontage={false}
              setDone={handleSetDone}
              onOpenModal={() => setIsOpenModal(true)}
            />
            <CardTabs key={forceRenderKey} id={id} />
            {visibleFormData && visibleFormData?.modalName === 'measurement' && (
              <MeasurementModal
                id={id}
                isModalOpen={isOpenModal}
                onCancel={handleCancel}
                onOk={handleOk}
              />
            )}
            {visibleFormData && visibleFormData?.modalName === 'manufacture' && (
              <ManufactureModal
                id={id}
                isModalOpen={isOpenModal}
                onCancel={handleCancel}
                onOk={handleOk}
              />
            )}
            {visibleFormData && visibleFormData?.modalName === 'delivery' && (
              <DeliveryModal
                id={id}
                isModalOpen={isOpenModal}
                onCancel={handleCancel}
                onOk={handleOk}
              />
            )}
          </DefaultJsonFormContext.Provider>
        </>
      )}
    </B2BLayout>
  )
}

export default memo(DetailPage)
